/**
 * SAB-1219: the player card's game log as the FULL SEASON -- one entry per NFL REG week,
 * 1..last, ascending. Driven by the franchise's schedule with stats joined in, where it used
 * to be driven by stat rows alone (so byes, future games and missed games simply vanished).
 *
 * ── ROW KINDS ───────────────────────────────────────────────────────────────
 *
 *   played    a stat row exists. TRS/ANT/SAB as before, expandable to the stat line and
 *             the breakdown. The STAT ROW WINS the week even when its game is not on the
 *             current franchise's schedule -- that is a traded player's past game.
 *   dnp       the current franchise's game is FINAL and the player has no stat row
 *             (inactive, injured, not yet on the team). Distinct from `upcoming` on
 *             purpose: dashes on both would make a missed game and a future game look
 *             identical -- the true-but-indistinguishable problem of the 0.0 row.
 *   upcoming  the game is not final yet. Opponent + dashes.
 *   bye       no game that week. Derived from the ABSENCE of a game row, never from
 *             nfl_franchises.bye_week_2026 (which hard-codes the year). Only emitted when
 *             the schedule actually loaded: an unloaded schedule is not an empty one.
 *
 * ── THE OPPONENT OF A PLAYED WEEK ───────────────────────────────────────────
 *
 * From the stat row's OWN game and the franchise the player played for in it
 * (nfl_player_game_stats.franchise_id, written by the sync from the SportRadar side). Only
 * when that column is NULL (not yet backfilled) does it fall back to the current franchise,
 * and only if the current franchise was actually one of the two sides; otherwise the
 * opponent is unknown and shown as such, never guessed.
 */

import { isGameFinal } from '@/lib/nflMatchupScoring';

export interface GameLogGame {
  id: string;
  week: number;
  home_franchise_id: string | null;
  away_franchise_id: string | null;
  scheduled: string | null;
  status: string | null;
}

/** The minimum a stat row must carry to be placed. The modal's GameStatRow satisfies it. */
export interface GameLogStat {
  id: string;
  week: number;
  trash_points: number;
  franchise_id?: string | null;
  game?: GameLogGame | null;
}

interface Side {
  opponentAlias: string | null;
  isHome: boolean | null;
  scheduled: string | null;
}

export type GameLogWeek<S extends GameLogStat = GameLogStat> =
  | ({ kind: 'played'; key: string; week: number; stat: S } & Side)
  | ({ kind: 'dnp'; key: string; week: number } & Side)
  | ({ kind: 'upcoming'; key: string; week: number } & Side)
  | { kind: 'bye'; key: string; week: number };

function sideOf(game: GameLogGame, franchiseId: string | null | undefined, aliasById: Record<string, string>): Side | null {
  if (!franchiseId) return null;
  const isHome = game.home_franchise_id === franchiseId ? true
    : game.away_franchise_id === franchiseId ? false
    : null;
  if (isHome === null) return null;
  const opp = isHome ? game.away_franchise_id : game.home_franchise_id;
  return { opponentAlias: opp ? (aliasById[opp] || null) : null, isHome, scheduled: game.scheduled };
}

const UNKNOWN_SIDE = (g: GameLogGame | null | undefined): Side =>
  ({ opponentAlias: null, isHome: null, scheduled: g?.scheduled ?? null });

export function buildGameLogWeeks<S extends GameLogStat>(args: {
  stats: S[];
  /** The CURRENT franchise's REG games this season. NULL = not loaded (emit no byes). */
  schedule: GameLogGame[] | null;
  /** max(week) of the season's REG games. NULL = unknown; the log then ends at the last known week. */
  lastWeek: number | null;
  currentFranchiseId: string | null;
  aliasById: Record<string, string>;
}): GameLogWeek<S>[] {
  const { stats, schedule, lastWeek, currentFranchiseId, aliasById } = args;

  const statsByWeek = new Map<number, S[]>();
  for (const s of stats) {
    const list = statsByWeek.get(s.week) ?? [];
    list.push(s);
    statsByWeek.set(s.week, list);
  }
  const gamesByWeek = new Map<number, GameLogGame[]>();
  for (const g of schedule ?? []) {
    const list = gamesByWeek.get(g.week) ?? [];
    list.push(g);
    gamesByWeek.set(g.week, list);
  }

  const last = Math.max(lastWeek ?? 0, ...stats.map((s) => s.week), ...(schedule ?? []).map((g) => g.week));
  const out: GameLogWeek<S>[] = [];

  for (let week = 1; week <= last; week++) {
    const played = statsByWeek.get(week);
    if (played && played.length > 0) {
      // Two stat rows in one week is possible only across a trade between a Thursday and a
      // Monday game; keep both, in kickoff order, each keyed by its own row.
      const ordered = [...played].sort((a, b) => String(a.game?.scheduled ?? '').localeCompare(String(b.game?.scheduled ?? '')));
      for (const stat of ordered) {
        const game = stat.game ?? null;
        const side = game
          ? (stat.franchise_id
              ? sideOf(game, stat.franchise_id, aliasById)
              // Fallback (c), ONLY where the column is still NULL: the current franchise,
              // and only if it was one of the two sides of this very game.
              : sideOf(game, currentFranchiseId, aliasById))
          : null;
        out.push({ kind: 'played', key: `s:${stat.id}`, week, stat, ...(side ?? UNKNOWN_SIDE(game)) });
      }
      continue;
    }

    const games = gamesByWeek.get(week);
    if (games && games.length > 0) {
      for (const g of games) {
        const side = sideOf(g, currentFranchiseId, aliasById) ?? UNKNOWN_SIDE(g);
        const final = isGameFinal({ scheduled: g.scheduled, status: g.status, opponentAlias: null, isHome: null });
        out.push({ kind: final ? 'dnp' : 'upcoming', key: `g:${g.id}`, week, ...side });
      }
      continue;
    }

    // No stats and no game: a bye -- but only if we actually know the schedule.
    if (schedule !== null) out.push({ kind: 'bye', key: `b:${week}`, week });
  }
  return out;
}

/**
 * The 🔥 marker, in ascending order.
 *
 * MEANING (unchanged from the newest-first version): the player's CURRENT run -- the most
 * recent consecutive PLAYED weeks with TRS > 5, when that run is 3 or more long. Counted
 * over played weeks only, so a bye, a DNP or a future week neither extends nor breaks it
 * (they had no stat row before either). What changed is only how the run is found: by
 * walking played weeks from the latest backwards, instead of taking the first N rows of a
 * newest-first list. Returns the keys of the marked rows.
 */
export function hotStreakKeys<S extends GameLogStat>(weeks: GameLogWeek<S>[]): Set<string> {
  const played = weeks.filter((w): w is Extract<GameLogWeek<S>, { kind: 'played' }> => w.kind === 'played');
  const run: string[] = [];
  for (let i = played.length - 1; i >= 0; i--) {
    if (Number(played[i].stat.trash_points) > 5) run.push(played[i].key);
    else break;
  }
  return run.length >= 3 ? new Set(run) : new Set();
}
